import bannerPlaceholder from "assets/images/evento-banner.jpg";
import { Close } from "assets/svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  TTeamMatchAnswer,
  TTeamMatchQuestion,
  TTeamMatchResult,
} from "services/types";
import { useEventStore, useFeedStore, useGuestStore } from "store";
import CdtAppPublicApi from "cdt-app-public-api/dist";
import { clarity } from "react-microsoft-clarity";
import TopBar from "pages/FeedV2/TopBar";
import {
  clearTeamMatchProgress,
  confettiShoot,
  getTeamMatchProgress,
  saveTeamMatchProgress,
  Timer,
} from "./gamesUtils";
import GuestFormModal from "components/GuestFormModal";
import CreatePost from "pages/FeedV2/CreatePost";
import { TFeedEvent } from "cdt-app-public-api/dist/types";

export default function TeamMatch() {
  const { event, theme } = useEventStore();
  const { questions = [] } = event?.teamMatchGame || {};
  const {
    guest,
    games: { teamMatchResult },
    setTeamMatchResult,
    setGameStatus,
  } = useGuestStore();
  const [activeQuestion, setActiveQuestion] =
    useState<TTeamMatchQuestion | null>(null);
  const [answers, setAnswers] = useState<{
    [key: string]: TTeamMatchAnswer;
  }>(teamMatchResult?.answers || {});
  const [saving, setSaving] = useState(false);
  const [selectedOption, setSelectedOption] = useState<TTeamMatchAnswer | null>(
    null
  );
  const teamAScoreRef = useRef<number>(teamMatchResult?.teamAScore || 0);
  const teamBScoreRef = useRef<number>(teamMatchResult?.teamBScore || 0);
  const timerRef = useRef<number>(teamMatchResult?.timeSpentInSeconds || 0);
  const navigate = useNavigate();
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [shouldStartAfterModal, setShouldStartAfterModal] = useState(false);

  useEffect(() => {
    if (event?.id && guest?.id && !teamMatchResult) {
      const savedProgress = getTeamMatchProgress(event.id, guest.id);

      if (savedProgress) {
        setAnswers(savedProgress.answers);
        teamAScoreRef.current = savedProgress.teamAScore;
        teamBScoreRef.current = savedProgress.teamBScore;
        timerRef.current = savedProgress.timeSpent;
      }
    }
  }, [event?.id, guest?.id, teamMatchResult]);

  const sortedQuestions = useMemo(() => {
    if (!questions.length) return [];
    if (!event?.id || !guest?.id)
      return [...questions].sort(() => Math.random() - 0.5);

    const savedProgress = getTeamMatchProgress(event.id, guest.id);
    if (!teamMatchResult && savedProgress?.questions) {
      return savedProgress.questions;
    }

    const shuffled = [...questions].sort(() => Math.random() - 0.5);

    if (!teamMatchResult) {
      saveTeamMatchProgress(event.id, guest.id, {
        answers: {},
        currentQuestionIndex: 0,
        teamAScore: 0,
        teamBScore: 0,
        timeSpent: 0,
        questions: shuffled,
      });
    }

    return shuffled;
  }, [questions, event?.id, guest?.id, teamMatchResult]);

  const totalQuestions = sortedQuestions.length;

  const activeQuestionIndex = useMemo(() => {
    return sortedQuestions.findIndex((q) => q.id === activeQuestion?.id);
  }, [sortedQuestions, activeQuestion]);

  const currentStep = useMemo(() => {
    if (saving) return "saving";
    if (teamMatchResult) return "result";
    if (!activeQuestion) return "intro";

    return "question";
  }, [saving, activeQuestion, teamMatchResult]);

  const saveResult = useCallback(async () => {
    setSaving(true);

    const quizResult: TTeamMatchResult = {
      eventId: event?.id as string,
      eventCode: event?.code as string,
      guestId: guest?.id as string,
      guestName: guest?.name,
      guestPhotoStoragePath: guest?.photoStoragePath || null,
      questions: sortedQuestions,
      answers,
      teamAScore: teamAScoreRef.current,
      teamBScore: teamBScoreRef.current,
      totalQuestions,
      timeSpentInSeconds: timerRef.current,
      createdAt: new Date().toISOString(),
    };

    const cdtAppClient = new CdtAppPublicApi();
    const teamMatchResultCreated = await cdtAppClient.createTeamMatch(
      quizResult
    );
    setTeamMatchResult(teamMatchResultCreated as TTeamMatchResult);

    if (event?.id && guest?.id) {
      clearTeamMatchProgress(event.id, guest.id);
    }

    if (clarity.hasStarted()) {
      clarity.setTag("engagement_level_creator", "true");
    }

    setSaving(false);

    setGameStatus("team_match_congrats_seen", true);
    setTimeout(() => confettiShoot(), 1000);
  }, [
    event?.id,
    event?.code,
    guest?.id,
    guest?.name,
    guest?.photoStoragePath,
    sortedQuestions,
    answers,
    totalQuestions,
    setTeamMatchResult,
    setGameStatus,
  ]);

  const handleOptionClick = useCallback((option: TTeamMatchAnswer) => {
    setSelectedOption(option);
  }, []);

  const handleConfirmAnswer = useCallback(() => {
    if (!activeQuestion || !selectedOption || !event?.id || !guest?.id) return;

    const newAnswers = {
      ...answers,
      [activeQuestion.id]: selectedOption,
    };

    setAnswers(newAnswers);

    if (selectedOption?.team === "teamA") {
      teamAScoreRef.current += 1;
    } else {
      teamBScoreRef.current += 1;
    }

    const nextIndex = activeQuestionIndex + 1;
    const nextQuestion = sortedQuestions[nextIndex];

    if (nextQuestion) {
      saveTeamMatchProgress(event.id, guest.id, {
        answers: newAnswers,
        currentQuestionIndex: nextIndex,
        teamAScore: teamAScoreRef.current,
        teamBScore: teamBScoreRef.current,
        timeSpent: timerRef.current,
        questions: sortedQuestions,
      });
    }

    setSelectedOption(null);
    setActiveQuestion(nextQuestion);

    if (!nextQuestion) {
      clearTeamMatchProgress(event.id, guest.id);
      saveResult();
    }
  }, [
    activeQuestion,
    selectedOption,
    activeQuestionIndex,
    sortedQuestions,
    saveResult,
    answers,
    event?.id,
    guest?.id,
  ]);

  const onStartClick = useCallback(() => {
    if (!event?.id || !guest?.id) return;

    if (!guest?.name) {
      setGuestModalOpen(true);
      setShouldStartAfterModal(true);
      return;
    }

    const savedProgress = getTeamMatchProgress(event.id, guest.id);
    if (savedProgress && Object.keys(savedProgress.answers).length > 0) {
      const currentQuestion =
        savedProgress.questions[savedProgress.currentQuestionIndex];
      setActiveQuestion(currentQuestion);
      return;
    }

    setActiveQuestion(sortedQuestions[0]);
  }, [sortedQuestions, event?.id, guest?.id, guest?.name]);

  const handleGuestModalClose = useCallback(() => {
    setGuestModalOpen(false);

    if (shouldStartAfterModal && event?.id && guest?.id) {
      setShouldStartAfterModal(false);

      const savedProgress = getTeamMatchProgress(event.id, guest.id);
      if (savedProgress && Object.keys(savedProgress.answers).length > 0) {
        const currentQuestion =
          savedProgress.questions[savedProgress.currentQuestionIndex];
        setActiveQuestion(currentQuestion);
        return;
      }

      setActiveQuestion(sortedQuestions[0]);
    }
  }, [shouldStartAfterModal, event?.id, guest?.id, sortedQuestions]);

  const handleGameClose = useCallback(() => {
    if (!event?.id || !guest?.id) return;

    if (activeQuestion && Object.keys(answers).length > 0) {
      saveTeamMatchProgress(event.id, guest.id, {
        answers,
        currentQuestionIndex: activeQuestionIndex,
        teamAScore: teamAScoreRef.current,
        teamBScore: teamBScoreRef.current,
        timeSpent: timerRef.current,
        questions: sortedQuestions,
      });
    }

    setActiveQuestion(null);
    setAnswers({});
    setSelectedOption(null);
    teamAScoreRef.current = 0;
    teamBScoreRef.current = 0;

    if (event?.betaFeatures?.includes("feed")) {
      return navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  }, [
    navigate,
    event?.betaFeatures,
    activeQuestion,
    event?.id,
    guest?.id,
    answers,
    activeQuestionIndex,
    sortedQuestions,
  ]);

  useEffect(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("game_team_match", "engaged");
    }
  }, []);

  return (
    <div
      className={`${theme} h-full w-full flex justify-center items-center px-5 py-16`}
    >
      <div className="h-full w-full flex flex-col justify-start items-center">
        {/* Header */}
        <div className="flex w-full items-center justify-between mb-4 ">
          <h1
            className="m-0 p-0 text-center grow"
            style={{
              fontSize: "clamp(2rem, 9.5vw, 2.5rem)",
            }}
          >
            <FormattedMessage
              id="title.teamMatch"
              defaultMessage="Team Match"
            />{" "}
          </h1>
          {event?.betaFeatures?.includes("feed") && (
            <TopBar onClose={handleGameClose} />
          )}
          {currentStep !== "intro" &&
            !event?.betaFeatures?.includes("feed") && (
              <button
                className="flex items-center text-primary fixed right-3 top-3"
                onClick={handleGameClose}
              >
                <span className="text-lg">
                  <Close
                    width={36}
                    height={36}
                    color="none"
                    outline="#414141"
                  />
                </span>
              </button>
            )}
        </div>

        {currentStep === "intro" && <Intro onStartClick={onStartClick} />}

        {currentStep === "question" && activeQuestion && (
          <>
            {/* Subheader */}
            <div className="mb-8 flex justify-between w-full">
              {/* Question number */}
              <div>
                <span className="text-xl">
                  <span className="font-black text-black">
                    {activeQuestionIndex + 1}
                  </span>
                  /<span className="text-gray-500">{totalQuestions}</span>
                </span>
              </div>
              <Timer timerRef={timerRef} gameType="team_match" />
            </div>
            <Question
              key={activeQuestion.id}
              question={activeQuestion}
              selectedOption={selectedOption}
              onOptionClick={handleOptionClick}
            />

            {/* Confirm Button */}
            <div className="w-full mt-8 flex justify-center">
              <button
                onClick={handleConfirmAnswer}
                disabled={!selectedOption}
                className={`px-8 py-3 font-medium ${
                  selectedOption
                    ? `${theme.button}`
                    : `${theme.button} !bg-gray-200 !text-gray-400 cursor-not-allowed`
                }`}
              >
                <FormattedMessage
                  id="button.teamMatch.next"
                  defaultMessage="Próximo"
                />
              </button>
            </div>
          </>
        )}

        {currentStep === "saving" && (
          <p className="text-center">
            <FormattedMessage
              id="message.quiz.saving"
              defaultMessage="Salvando..."
            />
          </p>
        )}

        {currentStep === "result" && (
          <Result
            answers={answers}
            teamAScore={teamAScoreRef.current}
            teamBScore={teamBScoreRef.current}
            totalQuestions={totalQuestions}
          />
        )}
      </div>
      <GuestFormModal isOpen={guestModalOpen} onClose={handleGuestModalClose} />
    </div>
  );
}

const Intro = ({ onStartClick }: { onStartClick: () => void }) => {
  const { theme, event } = useEventStore();
  const { guest, getGameStatus, setGameStatus } = useGuestStore();
  const { teamAName = "Time Noiva", teamBName = "Time Noivo" } =
    event?.teamMatchGame || {};
  const hasSeenIntro = getGameStatus("team_match_intro_seen");
  const [countdown, setCountdown] = useState(3);
  const [isDisabled, setIsDisabled] = useState(true);

  const hasProgress = useMemo(() => {
    if (event?.id && guest?.id) {
      const savedProgress = getTeamMatchProgress(event.id, guest.id);
      return savedProgress && Object.keys(savedProgress.answers).length > 0;
    }
    return false;
  }, [event?.id, guest?.id]);

  useEffect(() => {
    if (hasSeenIntro) {
      setIsDisabled(false);
      setCountdown(0);
      return;
    }

    if (countdown === 0) {
      setIsDisabled(false);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, hasSeenIntro]);

  const handleStartClick = () => {
    if (!hasSeenIntro) {
      setGameStatus("team_match_intro_seen", true);
    }
    onStartClick();
  };

  return (
    <div className="flex flex-col justify-center items-center gap-3">
      {event?.customization?.theme.name === "rose" ? (
        <div className="relative wedding-rose">
          <img
            className="aspect-square object-cover w-full h-full"
            src={event?.bannerUrl || bannerPlaceholder}
            alt="banner-home"
          />
        </div>
      ) : (
        <div className="relative bg-white p-2 shadow-md">
          <img
            src={event?.bannerUrl || bannerPlaceholder}
            alt="event banner"
            className="aspect-[1/1.15] object-cover max-w-full"
          />
        </div>
      )}
      <h2 className="text-2xl font-medium">
        <FormattedMessage
          id="subtitle.teamMatch.intro"
          defaultMessage="Descubra o seu time"
        />
      </h2>
      {hasProgress ? (
        <p className="text-lg text-gray-500">
          <FormattedMessage
            id="message.teamMatch.progress"
            defaultMessage="O jogo ainda tá rolando! Continue e descubra seu time!"
          />
        </p>
      ) : (
        <p className="text-lg text-gray-500">
          <FormattedMessage
            id="message.teamMatch.intro"
            defaultMessage="Faça o teste e descubra se você faz parte do time {teamA} ou do time {teamB}!"
            values={{
              teamA: teamAName,
              teamB: teamBName,
            }}
          />
        </p>
      )}
      <div className="w-full flex flex-col justify-center items-center gap-8">
        <button
          onClick={handleStartClick}
          className={`px-10 py-3 ${theme.button}`}
          disabled={isDisabled}
        >
          <span className="text-center w-full">
            {hasSeenIntro ? (
              <FormattedMessage
                id="button.teamMatch.continue"
                defaultMessage="Continuar"
              />
            ) : (
              <FormattedMessage
                id="button.teamMatch.start"
                defaultMessage="Iniciar"
              />
            )}
            {isDisabled && countdown > 0 && (
              <span className=" ml-2 animate-pulse">{countdown}</span>
            )}
          </span>
        </button>
      </div>
    </div>
  );
};

const Question = ({
  question,
  selectedOption,
  onOptionClick,
}: {
  question: TTeamMatchQuestion;
  selectedOption: TTeamMatchAnswer | null;
  onOptionClick: (option: TTeamMatchAnswer) => void;
}) => {
  const { theme } = useEventStore();

  const handleOptionClick = (option: TTeamMatchAnswer) => {
    onOptionClick(option);
  };

  const [optionOne, optionTwo] = useMemo(() => {
    const options = [
      { team: "teamA", value: question.teamA },
      { team: "teamB", value: question.teamB },
    ];

    return options.sort(() => Math.random() - 0.5);
  }, [question]);

  return (
    <>
      {/* Question */}
      <div className={`${theme.card} card w-full p-6 mb-8`}>
        <h2 className="text-xl font-medium text-center">{question.text}</h2>
      </div>

      {/* Options */}
      <div className="gap-3 flex flex-col justify-start items-center w-full">
        <button
          onClick={() => handleOptionClick(optionOne)}
          className={`w-full min-h-24 max-h-40 px-4 py-3 flex items-center justify-center border rounded-2xl transition-all duration-200 ${
            selectedOption?.value === optionOne.value
              ? theme.name === "rose"
                ? `border-primary-rose bg-rose-50 border-2 scale-105 shadow-lg`
                : "border-gray-800 border-2 bg-gray-100 scale-105 shadow-lg"
              : theme.name === "rose"
              ? "border-rose-400 bg-white"
              : "border-black bg-white"
          }`}
        >
          <span
            className={`text-center text-lg no-select transition-all duration-200 ${
              selectedOption?.value === optionOne.value ? "font-semibold" : ""
            }`}
          >
            {optionOne.value}
          </span>
        </button>
        <Close
          width={40}
          height={40}
          color={theme.name === "rose" ? "rgb(247, 108, 111)" : "rgb(0, 0, 0)"}
          outline={
            theme.name === "rose" ? "rgb(247, 108, 111)" : "rgb(0, 0, 0)"
          }
        />
        <button
          onClick={() => handleOptionClick(optionTwo)}
          className={`w-full min-h-24 max-h-40 px-4 py-3 flex items-center justify-center border rounded-2xl transition-all duration-200 ${
            selectedOption?.value === optionTwo.value
              ? theme.name === "rose"
                ? "border-primary-rose border-2 bg-rose-50 scale-105 shadow-lg"
                : "border-gray-800 border-2 bg-gray-100 scale-105 shadow-lg"
              : theme.name === "rose"
              ? "border-rose-400 bg-white"
              : "border-black bg-white"
          }`}
        >
          <span
            className={`text-center text-lg no-select transition-all duration-200 ${
              selectedOption?.value === optionTwo.value ? "font-semibold" : ""
            }`}
          >
            {optionTwo.value}
          </span>
        </button>
      </div>
    </>
  );
};

const Result = (props: {
  answers: { [key: string]: TTeamMatchAnswer };
  teamAScore: number;
  teamBScore: number;
  totalQuestions: number;
}) => {
  const { answers, teamAScore, teamBScore, totalQuestions } = props;
  const { event, theme } = useEventStore();
  const { teamAName = "Time Noiva", teamBName = "Time Noivo" } =
    event?.teamMatchGame || {};
  const teamAScorePercent = useMemo(
    () => (teamAScore / totalQuestions) * 100,
    [teamAScore, totalQuestions]
  );
  const teamBScorePercent = useMemo(
    () => (teamBScore / totalQuestions) * 100,
    [teamBScore, totalQuestions]
  );
  const [createPost, setCreatePost] = useState(false);
  const { addOptimisticPost } = useFeedStore();
  const navigate = useNavigate();

  const winner = teamAScorePercent > teamBScorePercent ? teamAName : teamBName;

  const handleClosePostCreation = () => {
    setCreatePost(false);
  };

  const handlePostCreation = (post: TFeedEvent) => {
    if (post && event?.code) {
      addOptimisticPost(event.code, post);
    }
    setCreatePost(false);
    if (event?.betaFeatures?.includes("feed")) {
      navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  };

  return (
    <div className="flex flex-col justify-center items-center w-full">
      <h2 className="text-2xl font-medium">
        <FormattedMessage
          id="subtitle.teamMatch.result"
          defaultMessage="Resultado"
        />
      </h2>
      <div className="flex flex-col justify-center items-center py-4 gap-4 w-full">
        <h2 className="text-lg">
          <FormattedMessage
            id="message.teamMatch.result"
            defaultMessage="O seu time é <code>{team}</code>!"
            values={{
              team: winner,
              code: (text) => {
                return <span className="font-extrabold underline">{text}</span>;
              },
            }}
          />
        </h2>
        {/* Progress Bar Container */}
        <div className="flex flex-col justify-center items-center gap-1 w-full">
          <div className="flex justify-between items-center gap-2 w-full">
            <span className="text-lg">{teamAName}</span>
            <span className="text-lg">{teamBName}</span>
          </div>
          <div className="w-full h-8 rounded-full overflow-hidden bg-gray-200 relative">
            {/* Bride's Side */}
            <div
              className="h-full bg-rose-400 absolute left-0 top-0 transition-all duration-500 flex items-center justify-start text-white font-medium px-2"
              style={{ width: `${teamAScorePercent}%` }}
            >
              {teamAScorePercent && `${Math.round(teamAScorePercent)}%`}
            </div>
            {/* Groom's Side */}
            <div
              className="h-full bg-blue-400 absolute right-0 top-0 transition-all duration-500 flex items-center justify-end text-white font-medium px-2"
              style={{ width: `${teamBScorePercent}%` }}
            >
              {teamBScorePercent && `${Math.round(teamBScorePercent)}%`}
            </div>
          </div>
        </div>
        {/* Answers Tree */}
        <div className="flex flex-col justify-start items-start w-full gap-2 py-4">
          {Object.values(answers).map((answer, index) => (
            <div
              key={index}
              className="flex justify-start items-center gap-2 w-full"
            >
              <div
                className={`w-8 h-8 rounded-full ${
                  answer.team === "teamA" ? "bg-rose-400" : "bg-blue-400"
                }`}
              />
              <h2 className="text-sm text-left">{answer.value}</h2>
            </div>
          ))}
        </div>
        {event?.betaFeatures?.includes("feed") && (
          <button
            className={`px-8 py-3 mt-4 ${theme?.buttonSecondary}`}
            onClick={() => setCreatePost(true)}
          >
            <FormattedMessage
              id="button.teamMatch.share"
              defaultMessage="Compartilhar Resultado"
            />
          </button>
        )}
      </div>

      <CreatePost
        createPost={createPost}
        setPost={setCreatePost}
        onClose={handleClosePostCreation}
        onPostCreated={handlePostCreation}
        frameStyle="team-match"
        gameTag="team-match"
        gameResultText={`Eu descobri que faço parte do ${winner} na Batalha dos Noivos! E você, de qual time é? #BatalhaDosNoivos`}
      />
    </div>
  );
};
